<template>
  <div class="container">
    <div class="header d-flex flex-wrap justify-content-between">

      <Search
        v-model="filters.name"
        :focus="true"
        :inside-card="true"
        @search="onSearch"
        @clearSearch="onSearch"
        class="mb20"
      />

      <info-tip 
        :tip="shortcutKeyCodeLabel"
        :show-icon="false"
        class="pull-right"
      >
        <button class="btn btn-carma btn-square" @click="addRow">
            <i class="fas fa-plus"></i> {{ $t('group.buttons.add_group') }}
        </button>
      </info-tip>
    </div>

    <div class="card clearfix">
      <datatable
        ref="table"
        :data="datatable"
        :loading="loadingFirstPage"
      />
      <div class="loading-next-page" v-if="loadingNextPage"><loader /></div>
    </div>

    <side-panel v-if="showAddGroup" :onClose="closePanel">
      <template>
        <load-content :loading="updating">
          <form-renderer
            id="create-group-form"
            title="group.form.create.title"
            submitButtonLabel="group.form.create.save"
            :fields="['name', 'files_upload_excel']"
            @onSave="createNewGroup"
          >
          </form-renderer>
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="showRemoveGroup" :onClose="closePanel">
      <template>
        <load-content :loading="updating">
          <confirm-delete
            :label="removeGroupObj.name"
            :onDelete="() => removeArticleGroup(removeGroupObj)"
          />
        </load-content>
      </template>
    </side-panel>

    <confirmation-modal
      v-if="showAfrRemoveConfirmation"
      :headerText="$t('group.confirmation.delete_group.title')"
      :message="$t('group.confirmation.delete_group.message', { id: afrRules.map(rule => rule.afr_rule_id).join(', '), name: afrRules.map(rule => rule.afr_rule_name).join(', ' ) || '' })"
      :showDeleteButton="true"
      :force-tab-index="true"
      @confirmAction="confirmAfrLinkedGroupDeletion"
      @cancelAction="cancelAfrLinkedGroupDeletion"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Datatable from '../datatable';
import SidePanel from '../side-panel';
import ConfirmDelete from '../confirm-delete';
import LoadContent from '../load-content';
import runShortcut from "../../mixins/shortcuts";
import CodingHeadline from '../coding-headline';
import FormRenderer from "../form-renderer";
import InfoTip from "../info-tip";
import Search from "../../components/Search.vue";
import Loader from "../../components/loader";
import ConfirmationModal from "../confirmation-modal";

export default {
  name: 'ArticleGroup',
  components: {
    datatable: Datatable,
    LoadContent,
    SidePanel,
    ConfirmDelete,
    FormRenderer,
    InfoTip,
    Search,
    Loader,
    ConfirmationModal
  },
  mixins: [ runShortcut('addTag')],
  created() {
    window.addEventListener('scroll', this.scroll);
    this.resetPagination();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll);
  },
  data() {
    return {
      filters: {
        name: '',
      },
      isReady: false,
      pagination: null,
      showAddGroup: false,
      showRemoveGroup: false,
      showAfrRemoveConfirmation: false,
      newGroupName: '',
      removeGroupObj: {},
      afrLinkedGroupIdToDelete: null,
      afrRules: []
    }
  },
  computed: {
    ...mapState({
      articleGroup: state => state.articleGroup.articleGroup.data,
      articleGroupPagination: state => state.articleGroup.articleGroup.pagination,
      isBusy: state => state.articleGroup.articleGroup.isBusy,
      updating: state => state.articleGroup.articleGroup.updating,
      user: state => state.user.user.data,
    }),
    articleGroupData() { return this.articleGroup && this.articleGroup.data ? this.articleGroup.data : [] },
    projectUid() { return this.$route.params.project_uid },
    groupId() { return this.$route.params.group_id },
    loading() {
      return this.isBusy === true && this.articleGroup === null;
    },
    datatable() {
      const { projectUid, articleGroup } = this;
      const self = this;
      const body = articleGroup || []
      return {
        custom_components: { 'coding-headline': CodingHeadline },
        columns: [
          {
            key: 'name',
            label: 'group',
            orderable: true,
            style: { 'min-width': '300px' },
            input: {
              name: 'group',
              component: {
                name: 'coding-headline',
                getProps: (row) => {
                  return {
                    ...row,
                    onClick: (id) => {
                      self.$router.push(`/analysis/projects/${projectUid}/groups/${id}/articles?page=1`);
                    },
                  }
                }
              },
            }
          },
          {
            key: 'article_count',
            label: 'nº associated articles ',
            orderable: true,
            render: ({article_count, id} = {}) => {
              return (parseInt(article_count) > 0 ? <router-link style="color:#ec9148;" to={`/analysis/projects/${this.$route.params.project_uid}/groups/${id}/articles?page=1`}>{article_count}</router-link> : <span>0</span>)
            }
          }
        ],
        body,
        order: {
          column: 'id',
          direction: "desc",
          onChange: function(column, direction) {
            self.callOrderGroups({ order: column, direction: direction });
          }
        },
        addRow: {
          enabled: this.user.code_admin && this.articleGroup && this.articleGroup.length > 5,
          label: this.$t('group.buttons.add_group'),
          onClick: () => {
            self.showAddGroup = true;
          }
        },
        removeRow: {
          enabled: this.user.code_admin,
          onClick: (row) => {
            self.showRemoveGroup = true;
            return {
              ...self.removeGroupObj = row
            }
          }
        }
      }
    },
    loadingFirstPage() {
      return this.isBusy && this.pagination.page === 1;
    },
    loadingNextPage() {
      return this.isBusy && this.pagination.page > 1;
    }
  },
  methods: {
    ...mapActions({
      'getArticleGroup': 'articleGroup/getArticleGroup',
      'createGroup': "articleGroup/createGroup",
      'removeGroup': "articleGroup/removeGroup",
      'orderGroups': "articleGroup/orderGroups"
    }),
    onSearch() {
      this.pagination.page = 1;
      this.getArticleGroup({
        projectUid: this.projectUid,
        pagination: this.pagination,
        filters: { search: { name: this.filters.name }}
      })
        .finally(() => this.isReady = true);
    }, 
    closePanel: function() {
      this.showAddGroup = false;
      this.showRemoveGroup = false;
    },
    updateInput: function(value) {
      this.newGroupName = value;
    },
    removeArticleGroup: function(value) {
      const { projectUid } = this;
      const groupId = value.id;
      /**
       * Calls API to remove a group.
       * If the response contains AFR rules linked to the group,
       * store them and show a confirmation modal.
       * Otherwise, complete the removal and reset UI state.
       */
      this.removeGroup({ projectId: projectUid, groupId, afrRuleId: null })
        .then(async (response) => {
          const afrData = response?.data || response;
          if (afrData?.length) {
            this.afrLinkedGroupIdToDelete = groupId;
            this.afrRules = afrData;
            this.showAfrRemoveConfirmation = true;
            return;
          }

          this.showRemoveGroup = false;
          this.resetPagination();
        })
        .catch(error => {
          console.error(error);
        });
    },
    /**
     * Confirms deletion of a group linked to AFR rules.
     * Deletes the group and removes it from all associated AFR rules.
     * Resets UI state after completion.
     */
    async confirmAfrLinkedGroupDeletion() {
      const { projectUid, afrLinkedGroupIdToDelete, afrRules } = this;
      if (!afrLinkedGroupIdToDelete || !afrRules.length) {
        this.cancelAfrLinkedGroupDeletion();
        return;
      }

      try {
        await this.removeGroup({
          projectId: projectUid,
          groupId: afrLinkedGroupIdToDelete,
          afrRuleIds: this.afrRules.map(rule => rule.afr_rule_id)
        });

        this.showRemoveGroup = false;
        this.resetPagination();
      } catch (error) {
        console.error("[ERROR][confirmAfrLinkedGroupDeletion]:", error);
      } finally {
        this.cancelAfrLinkedGroupDeletion();
      }
    },
    cancelAfrLinkedGroupDeletion() {
      this.showAfrRemoveConfirmation = false;
      this.afrLinkedGroupIdToDelete = null;
      this.afrRules = [];
    },
    resetPagination: function () {
      this.pagination = { page: 1, perPage: 20, order: 'id', sort: 'asc' }
    },
    createNewGroup: function(data) {
      const { projectUid } = this;
      const group = new FormData;

      group.append('name', data.name);

      if (data.files_upload_excel && data.files_upload_excel.has('file'))
        group.append('file', data.files_upload_excel.get('file'));

        this.createGroup({ projectUid, group })
          .then(group => {
            if (group) {
              this.showAddGroup = false;
              this.resetPagination();
            }
          });
    },
    addRow: function(){
      this.showAddGroup = true
    },
    callOrderGroups(order) {
      this.orderGroups(order);
    },
    scroll() {
        if (this.$refs.table) {
        if (this.articleGroupPagination.currentPage < this.articleGroupPagination.totalPages) {
          const wHeight = window.innerHeight;
          const {top, height} = this.$refs.table.$el.getBoundingClientRect();
          const margin = 100;
          if (top * -1 + wHeight >= height - margin && this.isBusy == false) {
            this.pagination.page += 1;
          }
        }
      }
    },
  },

  watch: {
    pagination: {
      handler (val) {
        const { projectUid, filters } = this;
        const pagination = val;
        this.getArticleGroup({ projectUid, pagination, filters });
      },
      deep: true
    }
  }
};
</script>


<style lang="scss">
@import "../../styles/components/coding-admin/_article_group.scss";
</style>